import type { QueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/config/queries';
import { getLocalIsoDate } from '@/lib/dateUtils';

interface InvalidateWearableQueriesOptions {
  includeConnectionStatus?: boolean;
}

export async function invalidateWearableQueries(
  queryClient: QueryClient,
  userId: string,
  options: InvalidateWearableQueriesOptions = {},
) {
  const metricDate = getLocalIsoDate();
  const historicalMetricsRoot = QUERY_KEYS.HISTORICAL_METRICS('', '7d')[0];
  const historicalPointsRoot = QUERY_KEYS.HISTORICAL_POINTS('', '7d')[0];

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD_METRICS(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.METRICS(userId, metricDate) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_DATA(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_LAST_SYNC(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_SYNC_STATUS(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_SYNC_HISTORY(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POINTS(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.STREAK(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.HEALTH_SCORE(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.HEALTH_SCORE_CALIBRATION(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.WEARABLE_CONNECTIONS(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DAILY_RECOMMENDATION(userId) }),
    queryClient.invalidateQueries({
      predicate: (query) => {
        if (!Array.isArray(query.queryKey)) return false;
        const [keyRoot, keyUserId] = query.queryKey;
        return (
          keyUserId === userId &&
          (keyRoot === historicalMetricsRoot || keyRoot === historicalPointsRoot)
        );
      },
    }),
  ]);

  if (options.includeConnectionStatus) {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS() }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS_USER(userId) }),
    ]);
  }
}
